import { clampMaterialOpacity } from '@/animation/clampMaterialOpacity'
import { MIN_CLIP_DURATION_SEC } from '@/animation/timeline/clipConstraints'
import { normalizeClipToEndpointPair } from '@/animation/timeline/clipEndpoints'
import type { AnimationClip } from '@/types/animationClip'
import type { AnimationKeyframe } from '@/types/animationLayer'
import { createCompactRandomId } from '../../../shared/utils/randomUuid'

function createMigrationClipId(): string {
  return createCompactRandomId('clip-mig')
}

/**
 * 将 v1 图层的全局 `keyframes` 迁移为单条覆盖整条工程时长的 clip（关键帧时间写入 `timeInClip`）。
 *
 * @param keyframes - v1 全局时间关键帧
 * @param totalDurationSeconds - 工程总时长上限（用于 clip 时长）
 */
export function migrateKeyframesToClipsJson(
  keyframes: AnimationKeyframe[],
  totalDurationSeconds: number,
): AnimationClip[] {
  const maxDur = Math.min(600, Math.max(1, Number(totalDurationSeconds) || 10))
  if (!keyframes.length) {
    const empty: AnimationClip = {
      id: createMigrationClipId(),
      startSec: 0,
      durationSec: maxDur,
      easingPreset: 'linear',
      keyframes: [],
    }
    normalizeClipToEndpointPair(empty)
    return [empty]
  }
  const lastKf = Math.max(...keyframes.map((k) => k.timeSeconds))
  const durationSec = Math.max(
    MIN_CLIP_DURATION_SEC,
    Math.min(600, Math.max(maxDur, lastKf, MIN_CLIP_DURATION_SEC)),
  )
  const clip: AnimationClip = {
    id: createMigrationClipId(),
    startSec: 0,
    durationSec,
    easingPreset: 'linear',
    keyframes: keyframes
      .map((k) => ({
        id: k.id,
        timeInClip: Math.min(durationSec, Math.max(0, k.timeSeconds)),
        position: { ...k.position },
        rotationDeg: { ...k.rotationDeg },
        scale: { ...k.scale },
        materialOpacity: clampMaterialOpacity(k.materialOpacity),
      }))
      .sort((a, b) => a.timeInClip - b.timeInClip),
  }
  normalizeClipToEndpointPair(clip)
  return [clip]
}
